import { TenantOwnTermsDocument, TenantPayments } from '../../api/account/accountApiTypes';

/**
 * Which terms-and-conditions document a tenant's booking page shows, and why.
 *
 * ONE RULE, THREE COPIES. The end-user app decides this for real, in
 * `apps/end-user/src/defaults/config/components/termsDocument.ts`
 * (`termsDocumentFor` + `stationTermsVariantFor`); the backend refuses to take
 * a booking page live when it would answer `website`
 * (`new-backend/admin/src/lib/bookingPageTerms.ts`). The workspaces cannot
 * import from each other, so the rule is restated here and says so. **Change
 * one, change all three** — and `bookingPageTerms.test.ts` mirrors the
 * end-user cases so a divergence fails here rather than on a customer's screen.
 *
 * This copy answers only what the admin UI needs to SHOW. It renders no legal
 * text, which stays in one place: the end-user app.
 */

/** The use cases the kolula station document covers — its §II modular clauses. */
const STATION_TERMS_SERVICES = ['rental', 'storage', 'p2p', 'parcel'];

/** Entries in `services` that are not bookable use cases and carry no terms. */
const NOT_A_USE_CASE = ['voucher', 'moreToCome'];

/** kolula's own legal entity, normalised — mirrors `isKolulaOperated` in
 *  `new-backend/app/src/lib/tenantConfig.ts`, which is authoritative. */
const KOLULA_ORGANIZATION_PREFIX = 'kolulasolutions';

export interface BookingPageTerms {
    /** `own` — the tenant's own documents (upload or link each); `station` —
     *  the kolula document; `website` — a link to the tenant's own site. */
    document: 'own' | 'station' | 'website';
    /** Who the document names as the customer's counterparty. */
    counterparty: 'kolula' | 'tenant';
    /** Whether the paid or the free variant is shown. */
    paid: boolean;
    /** The use cases the document covers, in the tenant's own order. */
    useCases: string[];
    /** Use cases no kolula document covers yet — why `document` is `website`. */
    uncovered: string[];
}

export const resolveBookingPageTerms = (input: {
    organizationName?: string | null;
    services?: string[] | null;
    payments?: TenantPayments | null;
    ownTerms?: TenantOwnTermsDocument[] | null;
}): BookingPageTerms => {
    const useCases = (input.services ?? []).filter(
        (service) => !NOT_A_USE_CASE.includes(service),
    );
    const covered = useCases.filter((service) => STATION_TERMS_SERVICES.includes(service));
    const uncovered = useCases.filter((service) => !STATION_TERMS_SERVICES.includes(service));

    const counterparty = (input.organizationName ?? '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '')
        .startsWith(KOLULA_ORGANIZATION_PREFIX)
        ? 'kolula'
        : 'tenant';

    // The document is shown only when it covers EVERY use case the tenant
    // offers: terms silent on half of what a tenant sells still read as
    // complete. Free only when every covered use case is free; an unconfigured
    // one counts as paid.
    //
    // The tenant's own AGB wins over both (ADR-0003 amendment 2026-10-02).
    const stationOrWebsite = useCases.length > 0 && uncovered.length === 0 ? 'station' : 'website';
    const document = (input.ownTerms ?? []).length > 0 ? 'own' : stationOrWebsite;
    const paid =
        covered.length === 0
        || covered.some((service) => input.payments?.useCases?.[service]?.charging !== 'free');

    return { document, counterparty, paid, useCases: covered, uncovered };
};
